"use client";
import { useSupportAgentConfig } from "@/app/_components/SupportAgent/ConfigProvider";
import { defaultSupportAgentId } from "@/constants";
import Orb2 from "@ui/components/demos/ConvAIHero/Orb/Orb2";
import { getLocalStorage, setLocalStorage, LocalStorageKeys } from "@utilities/localStorage";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { InlineTerms } from "./InlineTerms";
import { useConversationalAIPageAgent } from "./Provider";

const InCall = dynamic(() => import("./InCall"), {
  ssr: false,
});

const containerVariants = cva("pointer-events-none fixed bottom-16 z-50 flex", {
  variants: {
    position: {
      center: "left-0 w-full justify-center",
      right: "right-16 justify-end",
    },
  },
  defaultVariants: {
    position: "center",
  },
});

export default function FloatingAgent({
  agentId = defaultSupportAgentId,
  position = "center",
  variant = "floating",
  buttonText,
  responsiveButtonText,
}: {
  agentId?: string;
  position?: "center" | "right";
  variant?: string;
  /** Custom button text - if not provided, uses default translation */
  buttonText?: string;
  /** Responsive button text - shows different text on mobile vs desktop */
  responsiveButtonText?: { mobile: string; desktop: string };
}) {
  const t = useTranslations("ClientKeys.conversational_ai_page");
  // 'initial' | 'in-call'
  const [viewState, setViewState] = useState<"in-call" | "initial">("initial");
  const [hasTransitioned, setHasTransitioned] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const { registerStartCallAction } = useConversationalAIPageAgent();
  const { setHasActiveCall } = useSupportAgentConfig();

  // Track active call state in context so agent stays visible during calls
  useEffect(() => {
    setHasActiveCall(viewState === "in-call");
  }, [viewState, setHasActiveCall]);

  // Reset on unmount to prevent layout agent from incorrectly showing
  useEffect(() => {
    return () => setHasActiveCall(false);
  }, [setHasActiveCall]);

  // Handler to start call
  const handleStartCall = () => {
    // Check if terms have already been accepted
    const termsAccepted = getLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS);
    if (termsAccepted) {
      setHasTransitioned(true);
      setViewState("in-call");
    } else {
      setShowTerms(true);
    }
  };

  const handleAgreeToTerms = () => {
    setLocalStorage(LocalStorageKeys.CONVAI_MARKETING_PAGE_TERMS, "true");
    setShowTerms(false);
    setHasTransitioned(true);
    setViewState("in-call");
  };

  const handleCloseTerms = () => {
    setShowTerms(false);
  };

  // Handler to end call
  const handleEndCall = () => setViewState("initial");

  // Register the start call action with the provider
  useEffect(() => {
    registerStartCallAction(handleStartCall);
  }, [registerStartCallAction]);

  return (
    <AnimatePresence mode='wait'>
      {viewState === "initial" && !showTerms && (
        <div className={containerVariants({ position })}>
          <m.button
            className='pointer-events-auto flex items-center gap-12 rounded-[30px] p-12 pl-[14px] pr-[20px] backdrop-blur-xl'
            layoutId='floating-agent'
            onClick={handleStartCall}
            style={{
              backgroundColor: "rgba(245, 242, 239, 0.8)",
              boxShadow: "0px 6px 16px 0px rgba(78, 50, 23, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.1)",
              height: "52px",
              minWidth: "fit-content",
              borderRadius: "30px",
            }}
            transition={{
              layout: { type: "spring", stiffness: 400, damping: 40, duration: 0.6 },
              default: { duration: 0.2 },
            }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            <m.div className='h-28 w-28' layoutId='orb'>
              <Orb2 size={28} />
            </m.div>
            <m.p
              animate={{ opacity: 1 }}
              className='f-paragraph-02 flex-shrink-0'
              exit={{ opacity: 0 }}
              initial={{ opacity: hasTransitioned ? 0 : 1 }}
              style={{ minWidth: "fit-content", whiteSpace: "nowrap" }}
              transition={{
                duration: 0.2,
                ease: "easeInOut",
                delay: hasTransitioned && viewState === "initial" ? 0.2 : 0,
              }}
            >
              {responsiveButtonText ? (
                <>
                  <span className='md:hidden'>{responsiveButtonText.mobile}</span>
                  <span className='hidden md:inline'>{responsiveButtonText.desktop}</span>
                </>
              ) : (
                (buttonText ?? t("floating_agent.talk_to_agent"))
              )}
            </m.p>
          </m.button>
        </div>
      )}
      {showTerms && (
        <div className={containerVariants({ position })}>
          <m.div
            className={clsx(
              "pointer-events-auto flex flex-col items-center rounded-[30px] backdrop-blur-xl",
              position === "right" ? "w-[80vw] max-w-[400px] md:w-full" : "w-full max-w-[400px]",
            )}
            layoutId='floating-agent'
            style={{
              backgroundColor: "rgba(245, 242, 239, 0.8)",
              boxShadow: "0px 6px 16px 0px rgba(78, 50, 23, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.1)",
              minHeight: "440px",
              borderRadius: "30px",
            }}
            transition={{
              layout: { type: "spring", stiffness: 400, damping: 40, duration: 0.6 },
            }}
          >
            <InlineTerms onAgree={handleAgreeToTerms} onCancel={handleCloseTerms} />
          </m.div>
        </div>
      )}
      {viewState === "in-call" && (
        <InCall agentId={agentId} onEndCall={handleEndCall} position={position} variant={variant} autoStart />
      )}
    </AnimatePresence>
  );
}
